// sayfer_web.ts
function uint8ArrayToBase64(arr: Uint8Array): string {
    return btoa(String.fromCharCode(...arr));
}

function base64ToUint8Array(str: string): Uint8Array {
    return new Uint8Array(atob(str).split('').map(c => c.charCodeAt(0)));
}

export class SayferWeb {
    private password: Uint8Array;

    constructor(password: string) {
        if (password.length < 8) {
            throw new Error('Пароль слишком короткий');
        }
        this.password = new TextEncoder().encode(password);
    }

    private async deriveKey(salt: Uint8Array): Promise<Uint8Array> {
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            this.password,
            { name: 'PBKDF2' },
            false,
            ['deriveBits']
        );
        const key = await crypto.subtle.deriveBits(
            {
                name: 'PBKDF2',
                salt: salt,
                iterations: 100000,
                hash: 'SHA-256'
            },
            keyMaterial,
            256 // 256 бит
        );
        return new Uint8Array(key);
    }

    async encrypt(data: string): Promise<{ key: string }> {
        const salt = crypto.getRandomValues(new Uint8Array(16));
        const nonce = crypto.getRandomValues(new Uint8Array(12));
        const keyMaterial = await this.deriveKey(salt);
        const key = await crypto.subtle.importKey(
            'raw',
            keyMaterial,
            { name: 'AES-GCM' },
            false,
            ['encrypt']
        );
        const encodedData = new TextEncoder().encode(data);
        const encryptedData = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv: nonce },
            key,
            encodedData
        );
        const combined = new Uint8Array([
            ...salt,
            ...nonce,
            ...new Uint8Array(encryptedData)
        ]);
        const keyField = uint8ArrayToBase64(combined);
        return { key: keyField };
    }

    async decrypt(keyField: string): Promise<string> {
        const combined = base64ToUint8Array(keyField);
        const salt = combined.slice(0, 16);
        const nonce = combined.slice(16, 28);
        const encryptedData = combined.slice(28);
        const keyMaterial = await this.deriveKey(salt);
        const key = await crypto.subtle.importKey(
            'raw',
            keyMaterial,
            { name: 'AES-GCM' },
            false,
            ['decrypt']
        );
        const decryptedData = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv: nonce },
            key,
            encryptedData
        );
        return new TextDecoder().decode(decryptedData);
    }

    async encryptRecords(records: any[]): Promise<{ key: string }> {
        const data = JSON.stringify(records);
        return this.encrypt(data);
    }

    async decryptRecords(keyField: string): Promise<any[]> {
        const decryptedData = await this.decrypt(keyField);
        return JSON.parse(decryptedData);
    }
}

// Тест
export async function testSayferWeb() {
    const password = 'A'.repeat(128);
    const sayfer = new SayferWeb(password);

    const message = 'Hello from Nuxt!';
    const encrypted = await sayfer.encrypt(message);
    console.log('Encrypted:', encrypted);
    const decrypted = await sayfer.decrypt(encrypted.key);
    console.log('Decrypted:', decrypted);

    const records = Array.from({ length: 100 }, (_, i) => ({ id: i, value: `data_${i}` }));
    const encryptedRecords = await sayfer.encryptRecords(records);
    console.log('Encrypted records:', encryptedRecords);
    const decryptedRecords = await sayfer.decryptRecords(encryptedRecords.key);
    console.log('Decrypted records (first 3):', decryptedRecords.slice(0, 3));

    const from_back_test = 'bGkXJme9dUcUxujoYniDDYdBPbwOEXT2sP+FXdPUOIhZdfffTX0gMv8+3+i0iMf3Hf0Mpb5pbrw+N4PljguGYG8OZsyQrBVeHBj+Y70wKAeBMYqJfNCDBZr/pfZhPtar1NTbFTukph0JGVLjxQo5Nt1C/45hqzWQsqjgJ66Uhb2Ykcjocu+jtATwLsCk2XNyRoS/LKyNZF8fVL+Ue0C7x6hgrQMquF+6j/mJVaGxfG6ITc9w9VmwgYJbJxt/IyfihLnQCNh/NvuBmDbHk7o7BlIpjIyMqxWaFFOOZhYtbrZgySQy/d7oxhOteZA+rHw1iy3y1vF1GFM60OYYTWkVrNx3CW2tJVP9YubSDocBSHJqxq6Z+6pC5kdkXXJKv6vnmFpsiso/Q/gnUJPtia1Thlq7a658WDPd4/5aJfr037Tg7vQWBEfOlEgYmCEQvhl01OaT7Zcoe2k=';

    const decryptedFromBack = await sayfer.decrypt(from_back_test);
    console.log('Decrypted from back:', decryptedFromBack);
}